import React, {useEffect, useState} from 'react';
import EmptyView from './empty-view';
import Api from '../services/api';
import PaginationNova from './pagination-nova';
import WithdrawTransactionModel from '../models/withdrawTransaction';
import FullScreenLoader from './full-screen';
import WithdrawFormProp from './properties/withdraw-form-prop';
import ConfirmDialog from './confirm-dialog';

function WithdrawTransactions({setStatusMessage}: WithdrawFormProp): React.ReactElement {
    const [withdrawList, setWithdrawList] = useState([]);
    const [txType, setTxType] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(10);
    const [itemCount, setItemCount] = useState(10);
    const [resetPaging, setResetPaging] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
    const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmDialogData, setConfirmDialogData] = useState({});

    const setFailedRefundStatusWithMessage = (transactions: []) => {
        transactions.forEach((transaction) => {
            const tx: WithdrawTransactionModel = transaction;

            tx.failedStatus = !tx.withdrawTx && !(!tx.spendTx && !tx.payTx);
            // !((tx.spendTx && tx.payTx) || (!(tx.spendTx) && !(tx.payTx)));
            tx.ambiguousStatus = !tx.spendTx && !tx.payTx;
            tx.refundStatus = !!tx.refundTx;
            tx.message = '';

            if (tx.refundStatus) {
                tx.status = 'refunded';
            } else if (tx.ambiguousStatus) {
                tx.status = 'ambiguous';
                tx.message = 'Manual check required for this transaction. Not enough data found in database.';
            } else if (tx.failedStatus) {
                tx.status = 'failed';
            } else {
                tx.status = 'success';
            }
        });

        setWithdrawList(transactions);
    };

    const getTransactions = (type: string, limit: number, offset: number) => {
        setIsLoading(true);
        setStatusMessage('', '');
        Api.getWithdraw(type, limit, offset).then((result) => {
            if (result.error) {
                setStatusMessage(result.error.message, 'error');
            } else {
                setItemCount(result.data.length);
                if (result.data.length > pageCount) result.data.splice(-1);
                setFailedRefundStatusWithMessage(result.data);
            }
            setIsLoading(false);
        }).catch((error) => {
            setStatusMessage(error.message, 'error');
            setIsLoading(false);
        });
    };

    const handlePostRefundRetryClick = (spendTx: string, payTx: string, refund: boolean) => {
        setConfirmDialogData({
            spendTx,
            payTx,
            refund,
        });
        if (refund) {
            setConfirmDialogTitle('Confirm Refund!');
            setConfirmDialogMessage(`Are you sure you want to refund transaction. Spend Tx:${spendTx}, Pay Tx:${payTx}`);
        } else {
            setConfirmDialogTitle('Confirm Retry!');
            setConfirmDialogMessage(`Are you sure you want to retry transaction. Spend Tx:${spendTx}, Pay Tx:${payTx}`);
        }

        setShowConfirmDialog(true);
    };

    const handlePostRefund = (spendTx: string, payTx: string, refund: boolean) => {
        setIsLoading(true);
        setStatusMessage('', '');
        Api.refundWithdraw(spendTx, payTx, refund).then((result) => {
            if (result.error) {
                if (result.error.message?.refund?.length > 0) {
                    setStatusMessage(result.error.message?.refund.toString(), 'error');
                } else {
                    setStatusMessage(result.error.message, 'error');
                }
            } else if (result.data) {
                setStatusMessage('Refund performed without error', 'success');
            } else {
                setStatusMessage(result, 'error');
            }
            setIsLoading(false);
            window.scrollTo(0, 0);
        }).catch((error) => {
            setStatusMessage(error.message, 'error');
            setIsLoading(false);
            window.scrollTo(0, 0);
        });
    };

    const handleYesConfirmDialog = (data: any) => {
        setShowConfirmDialog(false);
        handlePostRefund(data.spendTx, data.payTx, data.refund);
    };

    const handlePageClick = (pageSize: number, pageNumber: number) => {
        setCurrentPage(pageNumber);
        setPageCount(pageSize);
    };

    const handleTabClick = (event: any, data: string) => {
        event.preventDefault();
        if (txType === data) return;
        setWithdrawList([]);
        setTxType(data);
        setResetPaging(true);
    };

    useEffect(() => {
        getTransactions(txType, pageCount + 1, (currentPage - 1) * pageCount);
        setResetPaging(false);
    }, [currentPage, pageCount, txType]);

    return (
        <div className="tab-container-one p-3 loading-wrapper">
            <ConfirmDialog
                title={confirmDialogTitle}
                message={confirmDialogMessage}
                show={showConfirmDialog}
                onHide={() => {
                    setShowConfirmDialog(false);
                }}
                data={confirmDialogData}
                handleYes={handleYesConfirmDialog}
                handleNo={() => {
                    setShowConfirmDialog(false);
                }}
            />
            <div className="row">
                <div className="col-6">
                    {' '}
                    <h4 className="mb-4">Withdraw Transactions</h4>
                </div>
                <div className="col-6"/>
            </div>
            <ul className="nav nav-tabs" id="withdrawTab" role="tablist">
                <li className={`nav-item ${txType === 'all' ? 'active' : ''}`}>
                    <a
                        className={`nav-link ${txType === 'all' ? 'active' : ''}`}
                        href="#tx"
                        role="tab"
                        aria-controls="tx-tab"
                        data-bs-toggle="tab"
                        onClick={((e) => handleTabClick(e, 'all'))}
                    >
                        All
                    </a>
                </li>
                <li className={`nav-item ${txType === 'failed' ? 'active' : ''}`}>
                    <a
                        className={`nav-link ${txType === 'failed' ? 'active' : ''}`}
                        href="#tx"
                        role="tab"
                        aria-controls="tx-tab"
                        data-bs-toggle="tab"
                        onClick={((e) => handleTabClick(e, 'failed'))}
                    >
                        Failed
                    </a>
                </li>
                <li className={`nav-item ${txType === 'refunded' ? 'active' : ''}`}>
                    <a
                        className={`nav-link ${txType === 'refunded' ? 'active' : ''}`}
                        href="#tx"
                        role="tab"
                        aria-controls="tx-tab"
                        data-bs-toggle="tab"
                        onClick={((e) => handleTabClick(e, 'refunded'))}
                    >
                        Refunded
                    </a>
                </li>
            </ul>
            <div className="tab-content p-3" style={{backgroundColor: '#f4f4f4'}}>
                <div className="tab-pane active" id="tx" role="tabpanel" aria-labelledby="tx-tab">
                    <h5 className="pt-3 pb-2">
                        List of
                        {' '}
                        {txType}
                        {' '}
                        transactions
                    </h5>
                    <div className={`accordion tab-${txType}`} id="withdrawTxs">
                        {isLoading ? <FullScreenLoader/> : ''}
                        {(withdrawList?.length > 0)
                            ? withdrawList?.map((withdraw: WithdrawTransactionModel, index) => (
                                <div key={`${withdraw.userId}_${withdraw.creationTimestamp}`}
                                     className="accordion-item mb-2 border-1" style={{borderTop: '1px solid #dfdfdf'}}>
                                    <h2 className={`accordion-header ${withdraw.status}`}
                                        id={`withdraw-heading-${index}`}>
                                        <button
                                            className="accordion-button d-block collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target={`#withdraw-${index}`}
                                            aria-expanded="false"
                                            aria-controls={`withdraw-${index}`}
                                        >
                      <span>
                        User Id:
                          {' '}
                          {withdraw.userId}
                      </span>
                                            {' | '}
                                            <span>
                        {' '}
                                                {withdraw.userSpendAddress}
                      </span>
                                            {withdraw.failedStatus && txType === 'all' ?
                                                <span style={{fontSize: '14px'}}
                                                      className={`accordion-header fst-italic ${txType === 'all' ? '' : 'd-none'}`}>(Failed</span> : ''}
                                            {withdraw.failedStatus && withdraw.refundStatus && txType === 'all' ?
                                                <span style={{fontSize: '14px'}}
                                                      className="p-1  fst-italic">&</span> : ''}
                                            {!withdraw.failedStatus && withdraw.refundStatus && txType === 'all' ?
                                                <span style={{fontSize: '14px'}} className="  fst-italic">(</span> : ''}
                                            {withdraw.refundStatus && txType === 'all' ?
                                                <span style={{fontSize: '14px'}}
                                                      className={`accordion-header  fst-italic ${txType === 'all' ? '' : 'd-none'}`}>Refunded</span> : ''}
                                            {(withdraw.failedStatus || withdraw.refundStatus) && txType === 'all' ?
                                                <span className=" fst-italic" style={{fontSize: '14px'}}>)</span> : ''}
                                            <span
                                                className="float-end fst-italic small">{withdraw.creationTimestamp}</span>
                                        </button>
                                    </h2>
                                    <div
                                        id={`withdraw-${index}`}
                                        className="accordion-collapse collapse"
                                        style={{backgroundColor: '#fff'}}
                                        aria-labelledby={`withdraw-heading-${index}`}
                                    >
                                        <div className="row ps-3 pb-0 pe-3">
                                            <div className="col">
                                                <div className="row">
                                                    <div className="col-md-6 pt-3">
                                                        <div className="row ps-3 pt-2 pe-2">
                                                            <div className="col-3">
                                                                Asset Id
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.assetId}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <hr className="w-50 mb-0" style={{marginLeft: '2rem', color: '#d8d8d8'}}/>
                                        <div className="row ps-3 pb-0 pe-3">
                                            <div className="col">
                                                <div className="row">
                                                    <div className="col-md-6 pt-3 pb-3">
                                                        <h6 className="text-uppercase ps-3">Spend</h6>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                User Address
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.userSpendAddress}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Pool Address
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.poolSenderAddress}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Blockchain
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.spendBlockchain}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Token Path
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.spendTokenPath}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Transaction
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.spendTx}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Contract
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.spendContract}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6 pt-3 pb-3">
                                                        <h6 className="text-uppercase ps-3">Receive</h6>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                User Address
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.userReceiveAddress}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Pool Address
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.poolReceiverAddress}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Blockchain
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.receiveBlockchain}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Token Path
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.receiveTokenPath}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Transaction
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.withdrawTx}
                                                            </div>
                                                        </div>
                                                        <div className="row ps-3 pt-2 pb-2 pe-2">
                                                            <div className="col-3">
                                                                Contract
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.receiveContract}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <hr className="w-50" style={{marginLeft: '2rem', color: '#d8d8d8'}}/>
                                        <div className="row ps-3 pb-0 pe-3">
                                            <div className="col">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="row ps-3 pe-2">
                                                            <div className="col-3">
                                                                Pay Transaction
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.payTx}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="row ps-3 pe-2">
                                                            <div className="col-3">
                                                                Pay Contract
                                                            </div>
                                                            <div className="col-9 address-100">
                                                                {withdraw.payContract}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row ps-3 pt-2 pb-0 pe-3">
                                            <div className="col">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="row ps-3 pe-2">
                                                            <div className="col-6">
                                                                Spend Confirmation Status
                                                            </div>
                                                            <div className="col-6 address-100">
                                                                {withdraw.hasSpendConfirmationStatus}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row ps-3 pt-2 pb-0 pe-3">
                                            <div className="col">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="row ps-3 pe-2">
                                                            <div className="col-6">
                                                                Refund Transaction
                                                            </div>
                                                            <div className="col-6 address-100">
                                                                {withdraw.refundTx}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <hr className="w-50 mb-2" style={{marginLeft: '2rem', color: '#d8d8d8'}}/>
                                        {(withdraw?.failedStatus || withdraw?.ambiguousStatus) && !withdraw?.refundTx
                                            ? (
                                                <div className="p-0">
                                                    {withdraw.failedStatus
                                                        ? (
                                                            <div className="row pt-3 pb-3 pe-2 mb-3"
                                                                 style={{paddingLeft: '2rem'}}>
                                                                <div className="col">
                                                                    <a
                                                                        href="#refund"
                                                                        type="button"
                                                                        data-bs-toggle="tooltip"
                                                                        data-bs-placement="top"
                                                                        title="Initiate Withdraw"
                                                                        className="btn btn-dark me-2"
                                                                        onClick={() => handlePostRefundRetryClick(
                                                                            withdraw.spendTx,
                                                                            withdraw.payTx,
                                                                            true,
                                                                        )}
                                                                    >
                                                                        Refund
                                                                    </a>
                                                                    <a
                                                                        href="#reprocess"
                                                                        type="button"
                                                                        data-bs-toggle="tooltip"
                                                                        data-bs-placement="top"
                                                                        title="Restart process"
                                                                        className="btn btn-dark"
                                                                        onClick={() => handlePostRefundRetryClick(
                                                                            withdraw.spendTx,
                                                                            withdraw.payTx,
                                                                            false,
                                                                        )}
                                                                    >
                                                                        Reprocess
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        ) : ''}

                                                </div>
                                            ) : ''}
                                        {withdraw?.message
                                            ? (
                                                <div className="row pt-2 pb-3 pe-2 mb-3" style={{paddingLeft: '2rem'}}>
                                                    <div className="col fst-italic">
                                                        <span className="pe-2">Note:</span>
                                                        {withdraw?.message}
                                                    </div>
                                                </div>
                                            )
                                            : ''}
                                    </div>
                                </div>
                            )) : (
                                <div className="p-2" style={{backgroundColor: '#fff'}}>
                                    {!isLoading ? <EmptyView title="Transactions not found!"
                                                             desc="Seems like there are no transactions in database"/> :
                                        <EmptyView title="Loading.." desc=""/>}
                                </div>
                            )}
                    </div>
                </div>
            </div>
            <div className={"mt-4 mb-4"}>
                <PaginationNova itemCount={itemCount} onPageClick={handlePageClick} reset={resetPaging}/>
            </div>
            <div className=" row legend mb-4">
                <h6 className="pb-2">Legend</h6>
                <div className="col-1">
                    <div className="color-box success "/>
                    <div className="pt-1">Success</div>
                </div>
                <div className="col-1">
                    <div className="color-box failed "/>
                    <div className="pt-1">Failed</div>
                </div>
                <div className="col-1">
                    <div className="color-box retry "/>
                    <div className="pt-1">Invalid Data (Manual Check Required)</div>
                </div>
                <div className="col-1">
                    <div className="color-box refunded "/>
                    <div className="pt-1">Refunded</div>
                </div>
            </div>
        </div>
    );
}

export default WithdrawTransactions;
